import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import Modal from 'LuxKit/Modal/Modal'
import { useExperienceReservationExpiryTimer } from './useExperienceReservationExpiryTimer'
import { TRANSFER_QUOTE_COUNTDOWN_WINDOW_SECONDS } from 'lib/experiences/transferQuoteUtils'

const CountdownClockContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${rem(12)};
  justify-content: center;
`

const DescriptionAndInfoIcon = styled.div`
  align-items: center;
  display: flex;
`

const ClickableInfoIcon = styled(Clickable)`
  display: flex;
  margin-inline-start: ${rem(4)};
`

const TimeFixedWidth = styled.div`
  display: inline-block;
  min-width: 2ch;
  text-align: center;
`

function convertNumberToTwoIntegerDigits(number: number) {
  return String(number).padStart(2, '0')
}

interface Props {
  reservationExpiresAt: string
  isTransferQuote?: boolean
}

function ExperienceReservationCountdownClock({ reservationExpiresAt, isTransferQuote = false }: Props) {
  const intl = useIntl()
  const { timeRemaining } = useExperienceReservationExpiryTimer(reservationExpiresAt, isTransferQuote ? TRANSFER_QUOTE_COUNTDOWN_WINDOW_SECONDS : undefined)
  const [infoModalOpen, setInfoModalOpen] = useState(false)

  if (!timeRemaining) return undefined

  return (
    <>
      <CountdownClockContainer>
        <DescriptionAndInfoIcon>
          <BodyText variant="small" weight="bold">
            {isTransferQuote ? intl.formatMessage({
              id: 'pvFhvT_SOnjmbhdiYTgq',
              defaultMessage: 'Your transfer quote expires soon',
              description: 'Checkout countdown banner title shown while a provider transfer quote remains valid',
            }) : intl.formatMessage({
              id: '6tKzs75giCK-tR8KzEpp',
              defaultMessage: 'Your tickets are reserved',
              description: 'Checkout banner title shown while an experience ticket reservation is still active',
            })}
          </BodyText>
          <ClickableInfoIcon onClick={() => setInfoModalOpen(true)}>
            <LineInfoCircleIcon size="S" />
          </ClickableInfoIcon>
        </DescriptionAndInfoIcon>
        <MessagingLabel variant="urgency">
          <FormattedMessage
            id="HAXQNIm0e3hE3d_WMPp5"
            defaultMessage="{minutes} : {seconds} remaining"
            description="Checkout banner countdown showing minutes and seconds until an experience ticket reservation expires"
            values={{
              minutes: <TimeFixedWidth>{convertNumberToTwoIntegerDigits(timeRemaining.minutes)}</TimeFixedWidth>,
              seconds: <TimeFixedWidth>{convertNumberToTwoIntegerDigits(timeRemaining.seconds)}</TimeFixedWidth>,
            }}
          />
        </MessagingLabel>
      </CountdownClockContainer>
      <Modal
        title={isTransferQuote ? intl.formatMessage({
          id: 'cG7xQPjcgfuNmx0oCMxu',
          defaultMessage: 'Transfer quote',
          description: 'Title of the information modal explaining the transfer quote deadline in checkout',
        }) : intl.formatMessage({
          id: 'G4XMwpd5lea6RwGklArd',
          defaultMessage: 'Ticket reservation',
          description: 'Modal title explaining the checkout experience ticket reservation countdown banner',
        })}
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
      >
        <BodyText variant="medium">
          {isTransferQuote ? intl.formatMessage({
            id: 'YNUK2hyymW0qwYgj95Lp',
            defaultMessage: 'Your transfer quote is valid for a limited time. If it expires, you will need to choose your transfer again. Prices and availability may change.',
            description: 'Information modal explaining transfer quote validity and reselection when the quote expires',
          }) : intl.formatMessage({
            id: 'VLI4A_75fMPA6uOnscNM',
            defaultMessage: 'We hold your selected experience tickets for a short time while you complete checkout. If the reservation expires, you will need to choose your tickets again.',
            description: 'Modal body explaining why the checkout experience ticket reservation countdown is shown and what happens when it expires',
          })}
        </BodyText>
      </Modal>
    </>
  )
}

export default ExperienceReservationCountdownClock
